import React, { useMemo } from 'react'
import { useRefetchable } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import type { GridProps } from '@vtex/admin-ui'
import { Grid, csx, cx } from '@vtex/admin-ui'
import { useIntl } from 'react-intl'

import { IndicatorComparableCard } from './IndicatorComparableCard'
import type { IndicatorCards_indicators_refetch } from './__generated__/IndicatorCards_indicators_refetch.graphql'
import type { IndicatorCards_indicators$key } from './__generated__/IndicatorCards_indicators.graphql'
import { IndicatorCardsSkeleton } from './IndicatorCardsSkeleton'
import { ReadableMessageError } from '../../../../../core/screens/GenericError/ReadableMessageError'

export type IndicatorCardsContainerProps = GridProps

export const IndicatorCardsContainer = ({
  className = '',
  ...props
}: IndicatorCardsContainerProps) => {
  return (
    <Grid
      className={cx(
        csx({
          gridGap: '1rem',
          '@tablet': {
            gridGap: 7,
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          '@desktop': {
            gridTemplateColumns: 'repeat(3, 1fr)',
          },
        }),
        className
      )}
      {...props}
    />
  )
}

type IndicatorsCardsState = ReturnType<typeof useIndicatorsCardsState>

export const useIndicatorsCardsState = ({
  indicators,
  isLoading,
}: {
  indicators: IndicatorCards_indicators$key | null | undefined
  isLoading: boolean
}) => {
  const state = useRefetchable<
    IndicatorCards_indicators_refetch,
    IndicatorCards_indicators$key
  >(
    graphql`
      fragment IndicatorCards_indicators on Query
      @refetchable(queryName: "IndicatorCards_indicators_refetch") {
        viewer {
          brand {
            currencyCode
          }
        }
        store: node(id: $storeId) {
          ... on Store {
            salesIndicators(where: { period: $period }) {
              averageItemAmount
              averageItemsPerSale
              averageSalesAmount
            }
            __typename
          }
        }
        salesAssociate: node(id: $salesAssociateId) {
          ... on SalesAssociate {
            salesIndicators(
              where: { storeId: { eq: $storeId }, period: $period }
            ) {
              salesAmount
              salesQuantity
              totalItemsSold
              averageItemAmount
              averageItemsPerSale
              averageSalesAmount
            }
            __typename
          }
        }
      }
    `,
    indicators!
  )

  return useMemo(
    () => ({
      ...state,
      isLoading: isLoading || state.isLoading,
    }),
    [state, isLoading]
  )
}

type IndicatorCardsProps = {
  state: IndicatorsCardsState
}

export const IndicatorCards = ({ state }: IndicatorCardsProps) => {
  const { data, isLoading } = state

  const { formatMessage } = useIntl()

  if (
    isLoading ||
    data?.store?.__typename !== 'Store' ||
    data?.salesAssociate?.__typename !== 'SalesAssociate'
  ) {
    return <IndicatorCardsSkeleton />
  }

  const {
    store,
    salesAssociate,
    viewer: { brand },
  } = data

  if (salesAssociate.salesIndicators === null) {
    throw new ReadableMessageError(
      'sales-performance.indicator-cards.insuffiecient-permissions'
    )
  }

  if (!brand) {
    throw new ReadableMessageError('brand-not-found')
  }

  return (
    <IndicatorCardsContainer>
      <>
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.sales-amount',
          })}
          value={salesAssociate.salesIndicators.salesAmount}
          style="currency"
          currency={brand.currencyCode}
        />
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.sales-quantity',
          })}
          value={salesAssociate.salesIndicators.salesQuantity}
        />
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.total-items-sold',
          })}
          value={salesAssociate.salesIndicators.totalItemsSold}
        />
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.average-sales-amount',
          })}
          value={salesAssociate.salesIndicators.averageSalesAmount}
          currency={brand.currencyCode}
          comparisonValue={store.salesIndicators.averageSalesAmount}
          style="currency"
        />
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.average-items-per-sale',
          })}
          value={salesAssociate.salesIndicators.averageItemsPerSale}
          comparisonValue={store.salesIndicators.averageItemsPerSale}
        />
        <IndicatorComparableCard
          title={formatMessage({
            id: 'sales-performance.indicator-cards.average-sales-item-amount',
          })}
          value={salesAssociate.salesIndicators.averageItemAmount}
          currency={brand.currencyCode}
          comparisonValue={store.salesIndicators.averageItemAmount}
          style="currency"
        />
      </>
    </IndicatorCardsContainer>
  )
}
